import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Link from '@/lib/siteLink';
import { Search, Car, Bike, Zap, ChevronRight, MapPin, Fuel, Calendar, Lock, TrendingUp, Star, GitCompare, Newspaper, ExternalLink, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { vehicleApi, formatPrice, newsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { initGA, usePageTracking, storeUTMParams } from '@/lib/analytics';
import AdSlot from '@/components/AdComponents';
import { AutoHeader, AutoFooter } from '@/components/AutoLayout';
import LatestArticles from '@/components/LatestArticles';
import PricePulseBand from '@/components/PricePulseBand';
import { cdnUrl } from '@/components/OptimizedImage';
import SEO from '@/components/SEO';

// The car's own photo when we have one; otherwise its name on a plain tile. Never a stock photo of some other car.
function VehicleThumb({ vehicle, width }) {
  const [failed, setFailed] = useState(false);
  const src = vehicle.images?.[0];
  if (src && !failed) {
    return (
      <img src={cdnUrl(src, { width })} alt={vehicle.title} className="w-full h-full object-cover"
        loading="lazy" decoding="async" onError={() => setFailed(true)} />
    );
  }
  const make = vehicle.brand || vehicle.make || '';
  const name = vehicle.model || vehicle.title || '';
  return (
    <div className="w-full h-full flex flex-col justify-end p-4 bg-[#1c1c1c]" role="img" aria-label={vehicle.title}>
      {make && <span className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(10,80%,65%)]">{make}</span>}
      <span className="text-lg font-bold text-white leading-tight line-clamp-2">{name}</span>
    </div>
  );
}

export default function AutoHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [trendingVehicles, setTrendingVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchType, setSearchType] = useState('');
  const [searchBrand, setSearchBrand] = useState('');
  const [brands, setBrands] = useState([]);
  const [latestNews, setLatestNews] = useState([]);

  // Initialize GA and track page
  useEffect(() => {
    initGA('auto');
    storeUTMParams();
  }, []);
  
  usePageTracking('auto');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch brand list whenever vehicle type changes so the Bike dropdown only
  // shows bike brands, not car brands. Fixes CBP-11.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await vehicleApi.getBrands(searchType);
        if (!cancelled) {
          setBrands(Array.isArray(r.data?.brands) ? r.data.brands : []);
          setSearchBrand('');
        }
      } catch (e) {
        console.error('Failed to load brands:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [searchType]);

  const loadData = async () => {
    try {
      const [vehiclesRes, brandsRes, trendingRes] = await Promise.all([
        vehicleApi.list({ is_featured: true, limit: 6 }),
        vehicleApi.getBrands(),
        vehicleApi.getTrending(6)
      ]);
      setFeaturedVehicles(Array.isArray(vehiclesRes.data) ? vehiclesRes.data : []);
      setBrands(Array.isArray(brandsRes.data?.brands) ? brandsRes.data.brands : []);
      setTrendingVehicles(Array.isArray(trendingRes.data) ? trendingRes.data : []);
      // Load news separately (non-blocking)
      try { const nr = await newsApi.latest('auto', 4); setLatestNews(nr.data?.articles || []); } catch (err) { console.error('Failed to load news:', err); }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchType) params.set('type', searchType);
    if (searchBrand) params.set('brand', searchBrand);
    navigate(`/auto/listings?${params.toString()}`);
  };

  const vehicleTypes = [
    { id: 'car', label: 'Cars', icon: Car, count: '500+' },
    { id: 'bike', label: 'Bikes', icon: Bike, count: '300+' },
    { id: 'scooter', label: 'Scooters', icon: Zap, count: '150+' },
  ];

  return (
    <div className="auto-theme min-h-screen">
      <SEO
        title="Compare Cars, Bikes & Scooter Prices in India"
        description="Find and compare the latest prices for cars, bikes, and scooters in India. Get on-road prices, specs, reviews, and expert comparisons."
        keywords="car prices India, bike prices India, scooter prices, compare vehicles, on-road price, best cars 2026, new car launches India"
        canonical="https://carsbikesprices.com/auto"
      />
      <AutoHeader />

      {/* Hero Section */}
      {/* No photo: a stock car in the hero is somebody's real car, and the page loads faster without it. */}
      <section
        className="auto-hero min-h-[85vh] flex items-center pt-16"
        style={{ background: 'linear-gradient(135deg, #111111 0%, #1c1c1c 55%, #3a0f0c 100%)' }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-2xl">
            <h1 className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 animate-slide-up" data-testid="hero-title">
              Find Your Perfect<br />
              <span className="text-[hsl(10,80%,55%)]">Ride</span> Today
            </h1>
            <p className="text-lg text-gray-300 mb-10 animate-slide-up stagger-1" data-testid="hero-subtitle">
              Browse through thousands of verified vehicles. Cars, bikes, and scooters at the best prices.
            </p>

            {/* Search Panel */}
            <div className="glass-panel rounded-lg p-6 animate-slide-up stagger-2" data-testid="search-panel">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger className="h-12 border-gray-200" data-testid="search-type">
                    <SelectValue placeholder="Vehicle Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="bike">Bike</SelectItem>
                    <SelectItem value="scooter">Scooter</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={searchBrand} onValueChange={setSearchBrand}>
                  <SelectTrigger className="h-12 border-gray-200" data-testid="search-brand">
                    <SelectValue placeholder="Select Brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button 
                  onClick={handleSearch}
                  className="h-12 bg-[hsl(10,80%,55%)] hover:bg-[hsl(10,80%,45%)] text-white rounded-md font-medium"
                  data-testid="search-button"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search Vehicles
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PricePulseBand />

      {/* Vehicle Types */}
      <section className="py-20 bg-[hsl(210,20%,98%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-gray-900 mb-10" data-testid="browse-title">
            Browse by Category
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {vehicleTypes.map((type) => (
              <Link
                key={type.id}
                to={`/auto/listings?type=${type.id}`}
                className="group bg-white border border-gray-200 rounded-md p-8 hover:border-[hsl(10,80%,55%)] hover:shadow-lg transition-all duration-300"
                data-testid={`category-${type.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[hsl(210,40%,96%)] rounded-md flex items-center justify-center group-hover:bg-[hsl(10,80%,95%)] transition-colors">
                      <type.icon className="w-7 h-7 text-gray-700 group-hover:text-[hsl(10,80%,55%)] transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-manrope font-semibold text-lg text-gray-900">{type.label}</h3>
                      <p className="text-sm text-gray-500">{type.count} available</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[hsl(10,80%,55%)] group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <LatestArticles textOnly site="auto" title="Latest Auto Articles" basePath="/auto/blog" />

      {/* Ad Slot - After Hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <AdSlot site="auto" placement="hero_after" maxAds={1} />
      </div>

      {/* Featured Vehicles */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-gray-900" data-testid="featured-title">
              Featured Vehicles
            </h2>
            <Link to="/auto/listings" className="text-[hsl(10,80%,55%)] font-medium text-sm hover:underline" data-testid="view-all-link">
              View All
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-md overflow-hidden">
                  <div className="aspect-vehicle skeleton" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 skeleton rounded w-3/4" />
                    <div className="h-4 skeleton rounded w-1/2" />
                    <div className="h-6 skeleton rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredVehicles.map((vehicle, index) => (
                <Link
                  key={vehicle.id}
                  to={`/auto/vehicle/${vehicle.slug || vehicle.id}`}
                  className="vehicle-card bg-white border border-gray-200 rounded-md overflow-hidden animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`vehicle-card-${vehicle.id}`}
                >
                  <div className="aspect-vehicle relative overflow-hidden">
                    <VehicleThumb vehicle={vehicle} width={800} />
                    {vehicle.is_featured && (
                      <span className="absolute top-3 left-3 bg-[hsl(10,80%,55%)] text-white text-xs font-medium px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-manrope font-semibold text-gray-900 mb-2 line-clamp-2">
                      {vehicle.title}
                    </h3>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {vehicle.year}
                      </span>
                      <span className="flex items-center gap-1">
                        <Fuel className="w-4 h-4" />
                        {vehicle.fuel_type}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {vehicle.location}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded">
                        <Lock className="w-3 h-3" />
                        Enquire for Price
                      </span>
                      <span className="text-xs text-gray-400">
                        {vehicle.kms_driven?.toLocaleString('en-IN')} km
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Ad Slot - Between Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <AdSlot site="auto" placement="listings_between" maxAds={2} />
      </div>

      {/* Trending Now */}
      {trendingVehicles.length > 0 && (
        <section className="py-16 bg-[hsl(210,20%,98%)]" data-testid="trending-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                <TrendingUp className="w-7 h-7 text-[hsl(10,80%,55%)]" />
                Trending Now
              </h2>
              <Link to="/auto/listings" className="text-[hsl(10,80%,55%)] font-medium text-sm hover:underline">View All</Link>
            </div>
            <div className="scroll-x pb-2">
              {trendingVehicles.map((vehicle, idx) => (
                <Link
                  key={vehicle.id}
                  to={`/auto/vehicle/${vehicle.slug || vehicle.id}`}
                  className="w-[280px] sm:w-[320px] bg-white border border-gray-200 rounded-md overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`trending-vehicle-${vehicle.id}`}
                >
                  <div className="aspect-[16/10] relative overflow-hidden">
                    <VehicleThumb vehicle={vehicle} width={600} />
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> #{idx + 1}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-manrope font-semibold text-gray-900 line-clamp-1">{vehicle.title}</h3>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span>{vehicle.year}</span>
                      <span>{vehicle.fuel_type}</span>
                      {vehicle.average_rating > 0 && (
                        <span className="flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{vehicle.average_rating}</span>
                      )}
                    </div>
                    <div className="mt-3">
                      <span className="flex items-center gap-1 text-xs text-gray-500"><Lock className="w-3 h-3" />Enquire for Price</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Compare CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-900 rounded-xl p-8 sm:p-12 text-center" data-testid="compare-cta">
            <GitCompare className="w-10 h-10 text-[hsl(10,80%,55%)] mx-auto mb-4" />
            <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-white mb-3">Compare Vehicles</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Compare specs, prices, and ratings side by side to find the perfect vehicle for you.
            </p>
            <Button
              onClick={() => navigate('/auto/compare')}
              className="bg-[hsl(10,80%,55%)] hover:bg-[hsl(10,80%,45%)] text-white px-8 py-5"
              data-testid="compare-cta-btn"
            >
              <GitCompare className="w-4 h-4 mr-2" />
              Start Comparing
            </Button>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      {latestNews.length > 0 && (
        <section className="py-12 bg-gray-50" data-testid="news-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Newspaper className="w-6 h-6 text-blue-600" />
                <h2 className="font-manrope text-2xl font-bold text-gray-900">Latest Auto News</h2>
              </div>
              <Link to="/auto/news" className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1" data-testid="view-all-news">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Every story shows; a publisher picture is a bonus, and a missing or blocked one just drops out. */}
              {latestNews.map((article) => (
                <a key={article.id} href={article.url} target="_blank" rel="noopener noreferrer nofollow"
                  className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`home-news-${article.id}`}
                >
                  {article.image_url && (
                    <div className="aspect-video bg-gray-100 overflow-hidden">
                      <img src={cdnUrl(article.image_url, { width: 600 })} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
                    </div>
                  )}
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{article.source_name}</span>
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" />
                        {new Date(article.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">{article.title}</h3>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ad Slot - Before Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <AdSlot site="auto" placement="before_footer" maxAds={1} />
      </div>

      <AutoFooter />
    </div>
  );
}
